import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaskDetailsModal } from "@/components/tasks2/TaskDetailsModal";
import { NewTaskModal } from "./NewTaskModal";
import { Task2 } from "@/types/tasks";
import { useTasks } from "@/hooks/useTasks";
import { Bot, TrendingUp, AlertTriangle, CheckCircle, X, XCircle, Plus, Clock, User, Minus, ChevronDown, RefreshCw, Activity } from "lucide-react";

const priorityConfig = {
  high: { color: "text-red-600", bgColor: "bg-red-50", variant: "destructive" as const, label: "Alta" },
  medium: { color: "text-yellow-700", bgColor: "bg-yellow-100", variant: "outline" as const, label: "Média" },
  low: { color: "text-gray-400", bgColor: "bg-gray-100", variant: "secondary" as const, label: "Baixa" },
};

const statusConfig = {
  completed: { label: "Concluída", color: "text-success", icon: CheckCircle, variant: "default" as const },
  in_progress: { label: "Em Andamento", color: "text-warning", icon: Clock, variant: "secondary" as const },
  not_started: { label: "Não Iniciado", color: "text-muted-foreground", icon: Clock, variant: "outline" as const },
  waiting: { label: "Aguardando", color: "text-blue-600", icon: Clock, variant: "outline" as const },
  cancelled: { label: "Cancelada", color: "text-muted-foreground", icon: XCircle, variant: "outline" as const },
};

const suggestionTypeIcons = {
  schedule_optimization: TrendingUp,
  quality_alert: AlertTriangle,
  weather_alert: AlertTriangle,
  safety_recommendation: AlertTriangle,
  resource_optimization: Activity,
  default: CheckCircle
};

interface TasksAndSuggestionsProps {
  workId: string; // ID da obra atual
  onClose?: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
}

export function TasksAndSuggestions({ workId, onClose, onMinimize, isMinimized }: TasksAndSuggestionsProps) {
  const {
    tasks: allTasks,
    loading,
    updateTask,
    refetch
  } = useTasks();

  // Filter tasks by workId
  const tasks = allTasks.filter(task => task.workId === workId);

  // Calculate stats
  const taskStats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    notStarted: tasks.filter(t => t.status === 'not_started').length,
    overdue: tasks.filter(t => t.endDate && new Date() > t.endDate && t.status !== 'completed').length
  };

  const lastUpdated = new Date();

  // Sem sugestões inventadas: as análises da IA virão dos dados reais da obra
  // (o componente já possui empty state para lista vazia).
  const suggestions: Array<{
    id: number;
    type: string;
    title: string;
    description: string;
    priority: "high" | "medium" | "low";
    workId: string;
    dismissed: boolean;
  }> = [];

  const [selectedTask, setSelectedTask] = useState<Task2 | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);

  // Mock projects data for the modal
  const projects = [
    { id: "1", name: "Projeto Principal", color: "#0071CE" }
  ];

  const handleTaskClick = (task: Task2) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };

  const handleTaskUpdate = (updatedTask: Task2) => {
    // Persiste as edições (antes só dava refetch e descartava as mudanças).
    updateTask(updatedTask.id, updatedTask);
  };

  const handleNewTaskCreated = (newTask: Task2) => {
    // Task is automatically added by the useTasks hook
    // Just refetch to ensure we have the latest data
    refetch();
  };

  const handleDismissSuggestion = (suggestionId: number) => {
    // Handle suggestion dismissal (could be implemented with a separate hook)
    console.log('Dismissing suggestion:', suggestionId);
  };

  // Helper function para format da data
  const formatDate = (date?: Date) => {
    return date ? date.toLocaleDateString('pt-BR') : 'Sem prazo';
  };

  const formatLastUpdated = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      <Card className="shadow-card transition-shadow">
        <Tabs defaultValue="tasks" className="w-full">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <CardTitle className="text-lg font-semibold text-foreground">
                  Tarefas e Sugestões
                </CardTitle>
                {loading && (
                  <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <div className="flex items-center gap-1">
                {onMinimize && (
                  <button
                    onClick={onMinimize}
                    className="p-1 rounded hover:bg-muted transition-colors"
                    title={isMinimized ? "Maximizar" : "Minimizar"}
                  >
                    {isMinimized ? <ChevronDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                  </button>
                )}
                {onClose && (
                  <button
                    onClick={onClose}
                    className="p-1 rounded hover:bg-muted transition-colors"
                    title="Fechar"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            
            {!isMinimized && (
              <>
                
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="tasks" className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Tarefas ({taskStats.total})
                  </TabsTrigger>
                  <TabsTrigger value="suggestions" className="flex items-center gap-2">
                    <Bot className="w-4 h-4" />
                    Sugestões da IA ({suggestions.length})
                  </TabsTrigger>
                </TabsList>
              </>
            )}
          </CardHeader>

        {!isMinimized && (
          <CardContent className="pt-0 max-h-[32rem] overflow-y-auto">
            <TabsContent value="tasks" className="mt-0">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-foreground">Lista de Tarefas</h3>
                <Button 
                  variant="default" 
                  size="sm" 
                  className="gap-2"
                  onClick={() => setIsNewTaskModalOpen(true)}
                >
                  <Plus className="w-4 h-4" />
                  Nova Tarefa
                </Button>
              </div>
              
              <div className="space-y-2">
                {tasks.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhuma tarefa encontrada para esta obra</p>
                  </div>
                ) : (
                  tasks.map((task) => {
                    // Status/prioridade fora do mapa não podem derrubar o painel inteiro
                    // (o banco aceita 'cancelled', que não tinha entrada aqui).
                    const priority = priorityConfig[task.priority as keyof typeof priorityConfig] ?? priorityConfig.medium;
                    const status = statusConfig[task.status as keyof typeof statusConfig] ?? statusConfig.not_started;
                    const StatusIcon = status.icon;
                    
                    return (
                      <div 
                        key={task.id}
                        className="p-3 rounded-lg border border-border bg-card hover:shadow-card transition-shadow cursor-pointer"
                        onClick={() => handleTaskClick(task)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-foreground">{task.title}</h4>
                              <Badge 
                                variant={priority.variant} 
                                className={`text-xs ${task.priority === 'medium' ? 'bg-yellow-400 text-yellow-900 hover:bg-yellow-400' : ''}`}
                              >
                                {priority.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{task.description}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <User className="w-4 h-4" />
                              <span>{task.assignees[0]?.name || "Não atribuído"}</span>
                            </div>
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Clock className="w-4 h-4" />
                              <span>{formatDate(task.endDate)}</span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {task.setor}
                            </div>
                          </div>
                        
                          <div className="flex items-center gap-1">
                            <StatusIcon className={`w-4 h-4 ${status.color}`} />
                            <Badge variant={status.variant} className="text-xs">
                              {status.label}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </TabsContent>

            <TabsContent value="suggestions" className="mt-0">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-foreground">Sugestões Inteligentes</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Bot className="w-4 h-4 text-primary" />
                  <span>Powered by IA</span>
                </div>
              </div>
              
              <div className="space-y-2">
                {suggestions.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhuma sugestão disponível para esta obra</p>
                  </div>
                ) : (
                  suggestions.map((suggestion) => {
                    const Icon = suggestionTypeIcons[suggestion.type as keyof typeof suggestionTypeIcons] || suggestionTypeIcons.default;
                    const priority = priorityConfig[suggestion.priority as keyof typeof priorityConfig];
                    
                    return (
                      <div 
                        key={suggestion.id}
                        className="p-3 rounded-lg border border-border bg-card hover:shadow-card transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className={`p-2 rounded-lg ${priority.bgColor}`}>
                              <Icon className={`w-4 h-4 ${priority.color}`} />
                            </div>
                            <h4 className="font-medium text-foreground">{suggestion.title}</h4>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={() => handleDismissSuggestion(suggestion.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-3 pl-10">
                          {suggestion.description}
                        </p>
                        
                        <div className="flex items-center justify-between pl-10">
                          <div className="flex space-x-2">
                            <Button variant="default" size="sm" className="text-xs">
                              Aplicar
                            </Button>
                            <Button variant="outline" size="sm" className="text-xs">
                              Ver Detalhes
                            </Button>
                          </div>
                          <Badge variant={priority.variant} className="text-xs">
                            {priority.label}
                          </Badge>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </TabsContent>
          </CardContent>
        )}
        </Tabs>
    </Card>

      {/* Modal de Detalhes da Tarefa */}
      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          projects={projects}
          isOpen={isTaskModalOpen}
          onClose={() => {
            setIsTaskModalOpen(false);
            setSelectedTask(null);
          }}
          onUpdate={handleTaskUpdate}
          isFullscreen={false}
          onToggleFullscreen={() => {}}
        />
      )}

      {/* Modal de Nova Tarefa (condicional: desmonta ao fechar) */}
        {isNewTaskModalOpen && (
          <NewTaskModal
            open={isNewTaskModalOpen}
            onOpenChange={setIsNewTaskModalOpen}
            onTaskCreated={handleNewTaskCreated}
            defaultWorkId={workId}
          />
        )}
    </>
  );
}